import { useMemo, useState, type ReactNode } from 'react';
import {
  useReportsSummary,
  useEngagement,
  useCampaignReports,
  type ReportSummary,
  type DailyEngagementPoint,
  type CampaignReportRow,
} from '../../api/reports.api';
import { useCampaigns } from '../../api/campaigns.api';

/* ── Screen 08 — Org reports dashboard (wired in Sprint 11) ── */

// OutcomesCard / RecruiterTableCard / LinkiInsightsPanel were removed in the
// dummy-data cleanup pass — they relied on data the API doesn't produce
// (reply categorisation, per-user aggregation, AI summarisation). Bring them
// back together with the producing endpoints.

const DAY_PRESETS: { days: number; label: string }[] = [
  { days: 7,  label: 'Last 7 days' },
  { days: 14, label: 'Last 14 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
];

export function ReportsPage() {
  const [days, setDays] = useState<number>(14);
  // Empty string = "All campaigns". Real campaign IDs are cuids so they
  // never collide with the empty sentinel.
  const [campaignId, setCampaignId] = useState<string>('');

  // Date window is derived from `days` and re-computed when the user picks
  // a different preset so the API queries refresh in sync.
  const { dateFrom, dateTo } = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setUTCDate(to.getUTCDate() - (days - 1));
    from.setUTCHours(0, 0, 0, 0);
    return { dateFrom: from.toISOString(), dateTo: to.toISOString() };
  }, [days]);

  const filterParams = useMemo(
    () => ({
      date_from: dateFrom,
      date_to: dateTo,
      ...(campaignId ? { campaign_id: campaignId } : {}),
    }),
    [dateFrom, dateTo, campaignId]
  );

  const summaryQ = useReportsSummary(filterParams);
  const engagementQ = useEngagement({ ...filterParams, days });
  // The /reports/campaigns endpoint doesn't accept a campaign_id filter (it
  // *is* the per-campaign view), so we only pass the date range to it.
  const topCampaignsQ = useCampaignReports({
    date_from: dateFrom,
    date_to: dateTo,
    sort: 'reply_rate',
    limit: 5,
  });

  // Campaign picker options — full list (any status) so the user can scope
  // reports to a paused or completed campaign too.
  const { data: campaignsList } = useCampaigns({ limit: 100 });
  const campaignOptions = campaignsList?.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <Breadcrumb />
      <HeadingRow
        summary={summaryQ.data}
        days={days}
        onDaysChange={setDays}
        campaignId={campaignId}
        onCampaignChange={setCampaignId}
        campaignOptions={campaignOptions}
        topCampaigns={topCampaignsQ.data ?? []}
      />
      <MetricsRow summary={summaryQ.data} isLoading={summaryQ.isLoading} />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 'var(--space-6)' }}>
        <EngagementCard points={engagementQ.data ?? []} isLoading={engagementQ.isLoading} />
        <TopCampaignsCard campaigns={topCampaignsQ.data ?? []} isLoading={topCampaignsQ.isLoading} />
      </div>

      {/*
       * Outcomes breakdown, recruiter table, and Linki insights are
       * intentionally not rendered. Each needs data the API doesn't yet
       * produce:
       *   - Outcomes: reply categorisation (positive / nurture / not interested)
       *   - Recruiter table: per-user aggregation joined to JT user names
       *   - Linki insights: AI-generated narrative summary across the period
       * They're tracked under "rough / partial" in the scope doc; the
       * components below stay defined so reviving them is just a re-render.
       */}
    </div>
  );
}

/** Build a CSV blob from the current top-campaigns table and trigger a
 *  browser download. Client-side only — the server doesn't expose a CSV
 *  endpoint yet, and the dataset is small enough (≤100 rows) that doing it
 *  in JS keeps the surface minimal. */
function downloadCampaignsCsv(rows: CampaignReportRow[], dateFrom: string, dateTo: string) {
  const header = [
    'campaign',
    'type',
    'status',
    'sent',
    'delivered',
    'opened',
    'clicked',
    'replied',
    'bounced',
    'unsubscribed',
    'open_rate',
    'click_rate',
    'reply_rate',
    'bounce_rate',
  ];
  const escape = (v: string | number) => {
    const s = String(v);
    // RFC4180-ish: only quote when needed. Commas, quotes, and newlines
    // force quoting; embedded quotes get doubled.
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push([
      r.name,
      r.type,
      r.status,
      r.metrics.sent,
      r.metrics.delivered,
      r.metrics.opened,
      r.metrics.clicked,
      r.metrics.replied,
      r.metrics.bounced,
      r.metrics.unsubscribed,
      r.rates.open_rate.toFixed(4),
      r.rates.click_rate.toFixed(4),
      r.rates.reply_rate.toFixed(4),
      r.rates.bounce_rate.toFixed(4),
    ].map(escape).join(','));
  }
  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = `${dateFrom.slice(0, 10)}_to_${dateTo.slice(0, 10)}`;
  a.href = url;
  a.download = `engage-campaign-report-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ── Header ── */

function Breadcrumb() {
  return (
    <nav aria-label="Breadcrumb">
      <ol style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
        <li><a href="/dashboard" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textDecoration: 'none' }}>Engage</a></li>
        <li aria-hidden="true" style={{ color: 'var(--color-text-subtle)' }}><IconChevronRight size={12} /></li>
        <li aria-current="page" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>Reports</li>
      </ol>
    </nav>
  );
}

interface CampaignOption { id: string; name: string }

function HeadingRow({
  summary,
  days,
  onDaysChange,
  campaignId,
  onCampaignChange,
  campaignOptions,
  topCampaigns,
}: {
  summary?: ReportSummary;
  days: number;
  onDaysChange: (n: number) => void;
  campaignId: string;
  onCampaignChange: (id: string) => void;
  campaignOptions: CampaignOption[];
  topCampaigns: CampaignReportRow[];
}) {
  const sends = summary?.sent ?? null;
  const sendsText = sends == null ? '-' : sends.toLocaleString();

  const selectedCampaignName =
    campaignOptions.find((c) => c.id === campaignId)?.name ?? 'All campaigns';

  // Export keys off whatever the user is currently looking at — the top
  // campaigns query already accounts for the date range. If the user has
  // a single campaign selected the export will still be the org-wide top 5
  // for the date window; we don't filter the campaign reports endpoint by
  // campaign because that's the row the user is already focused on.
  const handleExport = () => {
    if (topCampaigns.length === 0) return;
    const to = new Date().toISOString();
    const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    downloadCampaignsCsv(topCampaigns, from, to);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
      <div>
        <h1 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-4xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)', lineHeight: 'var(--leading-tight)' }}>
          Reports
        </h1>
        <p style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-muted)' }}>
          Last {days} days · {sendsText} sends · {selectedCampaignName}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <SelectControl
          icon={<IconCalendar size={14} />}
          value={String(days)}
          onChange={(v) => onDaysChange(parseInt(v, 10))}
          ariaLabel="Date range"
        >
          {DAY_PRESETS.map((p) => (
            <option key={p.days} value={String(p.days)}>{p.label}</option>
          ))}
        </SelectControl>

        <SelectControl
          icon={<IconLayers size={14} />}
          value={campaignId}
          onChange={onCampaignChange}
          ariaLabel="Campaign filter"
        >
          <option value="">All campaigns</option>
          {campaignOptions.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </SelectControl>

        <SecondaryButton onClick={handleExport} disabled={topCampaigns.length === 0}>
          <IconDownload size={14} /><span style={{ marginLeft: 'var(--space-1)' }}>Export</span>
        </SecondaryButton>

        {/* Linki summary button removed — the Linki backend exposes /draft
         *  and /rewrite for email text only, not narrative AI summaries.
         *  Restore alongside an /linki/summarize-period endpoint. */}
      </div>
    </div>
  );
}

/** Native <select> styled to look like the DropdownButton it replaces. Keeps
 *  the click target behaving like a real form control (keyboard a11y, screen
 *  reader semantics) rather than reinventing a popover. */
function SelectControl({
  icon,
  value,
  onChange,
  ariaLabel,
  children,
}: {
  icon: ReactNode;
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span style={{ position: 'absolute', left: 'var(--space-3)', display: 'inline-flex', alignItems: 'center', pointerEvents: 'none', color: 'var(--color-text-muted)' }}>
        {icon}
      </span>
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: 'var(--btn-height)',
          padding: '0 var(--space-7) 0 var(--space-8)',
          borderRadius: 'var(--btn-radius)',
          backgroundColor: 'var(--color-action-secondary)',
          color: 'var(--color-action-secondary-text)',
          border: '1px solid var(--color-action-secondary-border)',
          fontSize: 'var(--btn-font-size)',
          fontWeight: 'var(--font-weight-medium)',
          fontFamily: 'var(--font-sans)',
          cursor: 'pointer',
          appearance: 'none',
        }}
      >
        {children}
      </select>
      <span style={{ position: 'absolute', right: 'var(--space-3)', display: 'inline-flex', alignItems: 'center', pointerEvents: 'none', color: 'var(--color-text-muted)' }}>
        <IconChevronDown size={12} />
      </span>
    </span>
  );
}

/* ── Metrics row (live) ── */

interface MetricCardModel { label: string; value: string; sub: string }

function MetricsRow({ summary, isLoading }: { summary?: ReportSummary; isLoading: boolean }) {
  const cards = summary ? cardsFromSummary(summary) : placeholderCards();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 'var(--space-4)' }}>
      {cards.map((m) => <MetricCard key={m.label} metric={m} loading={isLoading && !summary} />)}
    </div>
  );
}

function cardsFromSummary(s: ReportSummary): MetricCardModel[] {
  const fmtPct = (r: number) => `${(r * 100).toFixed(1)}%`;
  return [
    { label: 'Sends',      value: s.sent.toLocaleString(),  sub: `${s.delivered.toLocaleString()} delivered` },
    { label: 'Open rate',  value: fmtPct(s.open_rate),      sub: `${s.opened.toLocaleString()} opens` },
    { label: 'Reply rate', value: fmtPct(s.reply_rate),     sub: `${s.replied.toLocaleString()} replies` },
    { label: 'Bounce rate',value: fmtPct(s.bounce_rate),    sub: `${s.bounced.toLocaleString()} bounces` },
  ];
}

function placeholderCards(): MetricCardModel[] {
  return [
    { label: 'Sends',       value: '-', sub: '' },
    { label: 'Open rate',   value: '-', sub: '' },
    { label: 'Reply rate',  value: '-', sub: '' },
    { label: 'Bounce rate', value: '-', sub: '' },
  ];
}

function MetricCard({ metric, loading }: { metric: MetricCardModel; loading: boolean }) {
  return (
    <div style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', padding: 'var(--stat-card-padding)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', opacity: loading ? 0.55 : 1 }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', letterSpacing: 'var(--tracking-caps)', textTransform: 'uppercase' }}>{metric.label}</span>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--stat-card-number-size)', fontWeight: 'var(--stat-card-number-weight)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)', lineHeight: 'var(--leading-tight)' }}>{metric.value}</span>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{metric.sub || ' '}</span>
    </div>
  );
}

/* ── Engagement chart ── */

function EngagementCard({ points, isLoading }: { points: DailyEngagementPoint[]; isLoading: boolean }) {
  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', padding: 'var(--card-padding)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>
          Engagement over time
        </h2>
        <ChartLegend items={[
          { label: 'Sends',   color: 'var(--color-blue-100)' },
          { label: 'Opens',   color: 'var(--color-brand-indigo)' },
          { label: 'Replies', color: 'var(--color-brand-teal)' },
        ]} />
      </div>
      {points.length === 0
        ? <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            {isLoading ? 'Loading…' : 'No activity in the selected window yet.'}
          </div>
        : <BarLineChart points={points} />
      }
    </section>
  );
}

function BarLineChart({ points }: { points: DailyEngagementPoint[] }) {
  const w = 720, h = 240;
  const pad = { top: 16, right: 16, bottom: 32, left: 36 };
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;

  const sends = points.map((p) => p.sent);
  const opens = points.map((p) => p.opened);
  const replies = points.map((p) => p.replied);
  const labels = points.map((p) => p.date.slice(5));

  const max = Math.max(1, ...sends, ...opens, ...replies);
  const step = points.length > 1 ? innerW / (points.length - 1) : 0;
  const barW = Math.max(4, step * 0.5);

  const linePath = (vals: number[]) =>
    vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${(pad.left + i * step).toFixed(1)},${(pad.top + innerH - (v / max) * innerH).toFixed(1)}`).join(' ');

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(max * t));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" style={{ display: 'block' }}>
      {ticks.map((tv, i) => {
        const y = pad.top + innerH - (tv / max) * innerH;
        return (
          <g key={i}>
            <line x1={pad.left} x2={w - pad.right} y1={y} y2={y} stroke="var(--color-border-default)" strokeWidth={1} strokeDasharray={i === ticks.length - 1 ? '0' : '2 4'} />
            <text x={pad.left - 8} y={y + 4} textAnchor="end" fontSize="10" fontFamily="var(--font-sans)" fill="var(--color-text-subtle)">{tv}</text>
          </g>
        );
      })}

      {sends.map((v, i) => {
        const x = pad.left + i * step - barW / 2;
        const y = pad.top + innerH - (v / max) * innerH;
        const bh = (v / max) * innerH;
        return <rect key={i} x={x} y={y} width={barW} height={bh} fill="var(--color-blue-100)" rx="2" />;
      })}

      <path d={linePath(opens)}   fill="none" stroke="var(--color-brand-indigo)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <path d={linePath(replies)} fill="none" stroke="var(--color-brand-teal)"   strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

      {labels.map((l, i) => {
        if (i % 2 !== 0 && i !== labels.length - 1) return null;
        return <text key={i} x={pad.left + i * step} y={h - 10} textAnchor="middle" fontSize="10" fontFamily="var(--font-sans)" fill="var(--color-text-subtle)">{l}</text>;
      })}
    </svg>
  );
}

function ChartLegend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
      {items.map((it) => (
        <span key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)' }}>
          <span style={{ width: '10px', height: '10px', borderRadius: 'var(--radius-full)', backgroundColor: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

/* ── Top campaigns ── */

const TOP_CAMPAIGN_COLORS = [
  'var(--color-brand-blue)',
  'var(--color-brand-indigo)',
  'var(--color-brand-teal)',
  'var(--color-warning)',
  'var(--color-brand-navy)',
];

function TopCampaignsCard({ campaigns, isLoading }: { campaigns: CampaignReportRow[]; isLoading: boolean }) {
  // Only campaigns that have sent something — a 0/0 reply rate isn't worth
  // ranking. Sorted server-side already; we just trim to those with sends.
  const eligible = campaigns.filter((c) => c.metrics.sent > 0);
  const max = eligible.length > 0 ? Math.max(...eligible.map((c) => c.rates.reply_rate)) : 0;

  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', overflow: 'hidden' }}>
      <PanelHeader title="Top campaigns by reply rate" linkLabel="Compare all" linkTo="/campaigns" />
      {isLoading && eligible.length === 0 && (
        <div style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>Loading…</div>
      )}
      {!isLoading && eligible.length === 0 && (
        <div style={{ padding: 'var(--space-4)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
          No campaigns with sends yet.
        </div>
      )}
      <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {eligible.map((c, idx) => {
          const ratePct = c.rates.reply_rate * 100;
          const widthPct = max > 0 ? (c.rates.reply_rate / max) * 100 : 0;
          const color = TOP_CAMPAIGN_COLORS[idx % TOP_CAMPAIGN_COLORS.length];
          return (
            <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 48px', alignItems: 'center', gap: 'var(--space-3)' }}>
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
              <div style={{ height: '8px', borderRadius: 'var(--radius-full)', backgroundColor: 'var(--color-surface-muted)', overflow: 'hidden' }}>
                <div style={{ width: `${widthPct}%`, height: '100%', backgroundColor: color, borderRadius: 'var(--radius-full)' }} />
              </div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)', textAlign: 'right' }}>{ratePct.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* Outcomes / Recruiter table / Linki insights components removed — see
 * the comment in the page body above for the data dependencies they need
 * before they can come back. */

/* ── Shared ── */

function PanelHeader({ title, linkLabel, linkTo }: { title: string; linkLabel: string; linkTo: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-4) var(--space-5)', borderBottom: '1px solid var(--color-border-default)' }}>
      <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>{title}</h2>
      <a href={linkTo} style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-link)', textDecoration: 'none' }}>{linkLabel} →</a>
    </div>
  );
}

/* Th / Td / Mono / Avatar removed with the RecruiterTableCard delete — bring
 * them back if the recruiter aggregate view returns. */

function SecondaryButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return <button type="button" onClick={onClick} disabled={disabled} style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--btn-height)', padding: '0 var(--btn-padding-x)', borderRadius: 'var(--btn-radius)', backgroundColor: 'var(--color-action-secondary)', color: 'var(--color-action-secondary-text)', border: '1px solid var(--color-action-secondary-border)', fontSize: 'var(--btn-font-size)', fontWeight: 'var(--btn-font-weight)', fontFamily: 'var(--font-sans)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>{children}</button>;
}
/* DropdownButton + LinkiButton removed — header now uses native SelectControl
 * and the Linki summary feature isn't wired (no backend narrative endpoint). */

/* ── Icons ── */

function IconChevronRight({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>; }
function IconChevronDown({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>; }
function IconCalendar({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>; }
function IconLayers({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>; }
function IconDownload({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>; }
// IconSpark removed alongside the Linki summary button.
